import isEmpty from "lodash/isEmpty";
import {
  FiltersV2TermSearch,
  useFiltersState,
  useFiltersV2Actions,
  useFiltersV2Store
} from "PFApp/components/filters_v2";
import { FiltersV2StorePage } from "PFApp/components/filters_v2/stores/filters_v2_store/filters_v2_store.types";
import { SearchEntity } from "PFApp/components/search_autocomplete/search_autocomplete.types";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Alert } from "PFComponents/alert";
import BrainLoading from "PFComponents/brain_loading/brain_loading";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useMatchesInvalidate } from "PFCore/hooks/queries/matches/use_matches_invalidate";
import { fetchMatches } from "PFCore/services/matches";
import { Activity } from "PFTypes";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useResourceMatches } from "../../hooks/use_resource_matches";
import { RenderActionCell, RowSelection } from "../../resource_table_toggle";
import { MatchesActions } from "./matches_actions";
import { MatchesTable } from "./matches_table";
import { NoMatchesPlaceholder } from "./no_matches_placeholder";

type MatchesPanelProps = RowSelection & {
  excludedProfileIds?: number[];
  activity: Activity;
  pageKey: RecentSearchesPageKey;
  matchesFiltersStorePage: FiltersV2StorePage;
  onNamedResourceFallbackClick?: VoidFunction;
  renderNoMatchesActionNamedResourceFallback?: (onNamedResourceClick: VoidFunction) => React.ReactNode;
  renderActionCell?: RenderActionCell;
};

export const MatchesPanel = ({
  excludedProfileIds,
  activity,
  pageKey,
  onRowSelectionChange,
  rowSelectionState,
  onNamedResourceFallbackClick,
  renderNoMatchesActionNamedResourceFallback,
  renderActionCell
}: MatchesPanelProps) => {
  const { t } = useTranslation(["activities", "translation", "filters"]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { matchesResponse, matchesQueryParams, matchesOrder } = useResourceMatches({
    excludedProfileIds: new Set<number>(excludedProfileIds ?? []),
    activityId: activity.id
  });
  const matchesResponseIsEmpty = !matchesResponse.isFetching && isEmpty(matchesResponse.matchesData);

  const { invalidate: invalidateMatches } = useMatchesInvalidate();
  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();

  const handleMatchesRefresh = useCallback(() => {
    setIsRefreshing(true);

    fetchMatches(activity.id, {
      refresh: true,
      ...matchesQueryParams
    })
      .then(() => {
        invalidateMatches();
      })
      .catch((error) => {
        const errorMessage = getFirstError(error);
        showGrowl({
          message: errorMessage || t("translation:somethingWentWrong"),
          kind: "error"
        });
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  }, [activity.id, matchesQueryParams, invalidateMatches, showGrowl, getFirstError]);

  const isCompleted = activity.state === "complete";

  const { resetValues } = useFiltersV2Actions();
  const { areFiltersDefaultOrEmpty } = useFiltersState();

  const termFilter = useFiltersV2Store((state) => state.mainFilters[KnownFiltersV2.Term]);
  const matchesSearchEnabled = !!termFilter && !isCompleted && matchesResponse.matchesData.length > 0;

  return (
    <>
      <FiltersV2TermSearch
        disabled={!matchesSearchEnabled}
        autocompleteConfig={[{ entity: SearchEntity.Profile, useAsTerm: true }]}
        pageKey={pageKey}
        fullWidth
      />
      {!areFiltersDefaultOrEmpty && (
        <Alert
          title={t("activities:show.matches.filtersChangedWarning")}
          action={{
            text: t("filters:restoreAll"),
            icon: "history",
            kind: "text",
            onClick: () => resetValues()
          }}
          multiLineTitle
        />
      )}
      {!isCompleted && (
        <MatchesActions
          onRefresh={handleMatchesRefresh}
          isLoading={isRefreshing || matchesResponse.isFetching}
          lastRefreshAt={matchesResponse.lastRefreshAt}
          orderMeta={matchesOrder.orderMeta}
          onOrderChange={matchesOrder.handleChangeOrder}
        />
      )}
      {isRefreshing && (
        <BrainLoading text={t("activities:resourceTableToggle.matches.calculationsStarted")} />
      )}
      {!matchesResponse.isError && matchesResponseIsEmpty && (
        <NoMatchesPlaceholder
          activity={activity}
          onNamedResourceFallbackClick={onNamedResourceFallbackClick}
          renderNoMatchesActionNamedResourceFallback={renderNoMatchesActionNamedResourceFallback}
        />
      )}
      {!matchesResponse.isError && !isRefreshing && !matchesResponseIsEmpty && (
        <MatchesTable
          activity={activity}
          onRowSelectionChange={onRowSelectionChange}
          rowSelectionState={rowSelectionState}
          matchesResponse={matchesResponse}
          renderActionCell={renderActionCell}
        />
      )}
    </>
  );
};
